import React from 'react';

/**
 * Renders a meal slot value that can be EITHER:
 *  - a plain string (legacy single-eater): "Rajma Chawal"
 *  - {primary, variations: {profileName: "tweaked"}}  (mostly shared with tweaks)
 *  - {by_profile: {profileName: "their dish"}}        (fundamentally different)
 */
export function MealSlotDisplay({ value }) {
  if (!value) return null;
  if (typeof value === 'string') {
    return <p className="text-sm">{value}</p>;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value.by_profile && typeof value.by_profile === 'object') {
      return (
        <div className="space-y-1">
          {Object.entries(value.by_profile).map(([profile, dish]) => (
            <div key={profile} className="flex items-baseline gap-1.5">
              <span className="text-[10px] uppercase font-bold text-purple-300 shrink-0">{profile}:</span>
              <span className="text-sm">{dish}</span>
            </div>
          ))}
        </div>
      );
    }
    if (value.primary) {
      return (
        <div>
          <p className="text-sm">{value.primary}</p>
          {value.variations && Object.keys(value.variations).length > 0 && (
            <div className="mt-0.5 space-y-0.5">
              {Object.entries(value.variations).map(([profile, dish]) => (
                <div key={profile} className="text-[11px] text-neutral-400">
                  <span className="text-purple-300 font-semibold">{profile}:</span> {dish}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return <p className="text-sm text-neutral-500 italic">{JSON.stringify(value)}</p>;
  }
  return <p className="text-sm">{String(value)}</p>;
}

/** Flatten a slot value into a single string for rating / shopping list lookups. */
export function flattenSlotForRating(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (value.by_profile) return Object.values(value.by_profile).join(' / ');
    if (value.primary) return value.primary;
  }
  return String(value);
}

// Convert any meal slot value into a flat list of chip-displayable strings.
// Supports legacy string ("a, b, c"), arrays, and multi-eater dicts.
export function slotToChips(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(v => String(v).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(/,|\u2022/).map(s => s.trim()).filter(Boolean);
  }
  if (typeof value === 'object') {
    if (value.by_profile && typeof value.by_profile === 'object') {
      return Object.entries(value.by_profile).map(([p, d]) => `${p}: ${d}`);
    }
    if (value.primary) {
      const out = [String(value.primary).trim()].filter(Boolean);
      if (value.variations && typeof value.variations === 'object') {
        Object.entries(value.variations).forEach(([p, d]) => out.push(`${p}: ${d}`));
      }
      return out;
    }
  }
  return [];
}

// IMPORTANT: keep this in sync with backend `_dish_key` in routes/meals.py.
// Both functions must produce the SAME key for the same input — otherwise
// cache lookups via /api/meals/dish-images miss and the chip shows a stale
// "Generate" button even when the image is already cached.
// Algorithm: lowercase → trim → replace non [a-z0-9 ] with space → collapse
// whitespace → spaces → underscores → cap at 60 chars.
export const dishKeyOf = (name) =>
  (name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/ /g, '_')
    .slice(0, 60);
